import { Component, ElementRef, HostListener, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { NotificationService, NotificationQueryOptions, NotificationQueryResult } from '../../services/notification.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Notification } from '../../pages/notifications-list/notifications-list.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MessagingService } from '../../../../core/services/messaging.service';
import { Router } from '@angular/router';
import { TASK_DISPLAY_STORAGE_KEY } from '../../../../core/constants';

@Component({
  selector: 'app-notification-dropdown',
  template: `
    <div class="notification-wrapper">
      <button 
        class="icon-button notification-trigger" 
        (click)="toggleDropdown()" 
        [class.active]="isOpen()"
        aria-label="Notifications">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15 6.66667C15 5.33971 14.4732 4.06705 13.5355 3.12682C12.5979 2.1866 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.1866 6.46447 3.12682C5.52678 4.06705 5 5.33971 5 6.66667C5 12.5 2.5 14.1667 2.5 14.1667H17.5C17.5 14.1667 15 12.5 15 6.66667Z"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path
            d="M11.4417 17.5C11.2952 17.7526 11.0849 17.9622 10.8319 18.1079C10.5789 18.2537 10.292 18.3304 10 18.3304C9.70805 18.3304 9.42114 18.2537 9.16815 18.1079C8.91515 17.9622 8.70486 17.7526 8.55835 17.5"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span class="notification-badge" *ngIf="unreadCount() > 0">
          {{ unreadCount() > 99 ? '99+' : unreadCount() }}
        </span>
      </button>

      <div class="notification-dropdown" *ngIf="isOpen()" [@slideIn]>
        <!-- Header -->
        <div class="dropdown-header">
          <div class="header-title">
            <h3>Notifications</h3>
            <span class="unread-count" *ngIf="unreadCount() > 0">({{ unreadCount() }} unread)</span>
          </div>
          <div class="header-actions">
            <button 
              class="mark-all-read-btn" 
              (click)="markAllAsRead()" 
              [disabled]="unreadCount() === 0 || isMarkingAllRead()"
              *ngIf="unreadCount() > 0">
              {{ isMarkingAllRead() ? 'Marking...' : 'Mark all read' }}
            </button>
          </div>
        </div>

        <!-- Filters -->
        <div class="notification-filters">
          <button 
            class="filter-btn" 
            [class.active]="currentFilter() === 'all'" 
            (click)="setFilter('all')">
            All
          </button>
          <button
            class="filter-btn"
            [class.active]="currentFilter() === 'unread'"
            (click)="setFilter('unread')">
            Unread
            <span class="filter-count" *ngIf="currentFilter() !== 'unread' && unreadCount() > 0">
              {{ unreadCount() }}
            </span>
          </button>
          <button
            class="filter-btn"
            [class.active]="currentFilter() === 'transactions'"
            (click)="setFilter('transactions')">
            Transactions
          </button>
        </div>

        <!-- Loading State -->
        <div class="notification-loading" *ngIf="isLoading()">
          <div class="loading-spinner"></div>
          <span>Loading notifications...</span>
        </div>

        <!-- Empty State -->
        <div class="notification-empty" *ngIf="!isLoading() && notifications().length === 0">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="2"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="2"/>
            <path d="M1 1l22 22" stroke="currentColor" stroke-width="2"/>
          </svg>
          <p>{{ currentFilter() === 'unread' ? "You're all caught up!" : currentFilter() === 'transactions' ? ("None in your most recent " + TRANSACTIONS_SCAN_LIMIT + " notifications.") : "No notifications" }}</p>
        </div>

        <!-- Notifications List -->
        <div class="notification-list" *ngIf="!isLoading() && notifications().length > 0">
          <div 
            class="notification-item" 
            *ngFor="let notification of notifications(); trackBy: trackByNotificationId"
            [class.unread]="!notification.isRead"
            (click)="handleNotificationClick(notification)">
            
            <div class="notification-icon" [class]="'icon-' + getNotificationColor(notification.type)">
              <svg width="16" height="16">
                <use [attr.xlink:href]="'assets/icons/' + getNotificationIcon(notification.type) + '.svg'"></use>
              </svg>
            </div>

            <div class="notification-content">
              <div class="notification-header">
                <h4 class="notification-title">{{ notification.title }}</h4>
                <div class="notification-meta">
                  <span class="notification-time">{{ formatTimeAgo(notification.createdAt) }}</span>
                  <!-- <span class="notification-type">{{ notification.type.replace('_', ' ') | titlecase }}</span> -->
                </div>
              </div>
              <p class="notification-message">{{ notification.message }}</p>
            </div>

            <div class="notification-status">
              <span class="unread-indicator" *ngIf="!notification.isRead"></span>
            </div>
          </div>
        </div>

        <!-- Footer. Also shown on an empty Transactions result (not just a
             non-empty list) so a scan-window miss still hands the DWM a path
             to look further back, instead of a dead end. -->
        <div class="dropdown-footer" *ngIf="notifications().length > 0 || (!isLoading() && currentFilter() === 'transactions')">
          <button class="view-all-btn" (click)="closeDropdown()">
            View all notifications
          </button>
        </div>
      </div>
    </div>
  `,
  standalone: true,
  imports: [CommonModule, FormsModule],
  styleUrls: ['./notification-dropdown.component.scss'],
  animations: [
    trigger('slideIn', [
      state('void', style({ opacity: 0, transform: 'translateY(-10%)' })),
      state('*', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('void => *', [
        animate('200ms ease-out')
      ]),
      transition('* => void', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-10%)' }))
      ])
    ])
  ]

})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** The two DWM transaction-alert notification types (NotificationType enum). */
  private readonly TRANSACTION_NOTIFICATION_TYPES = ['TRANSACTION_INITIATED', 'TRANSACTION_ALERT'];

  /**
   * The backend's GET /notifications has no type filter (confirmed against
   * iArista-node's notification.validation.ts/.controller.ts) — it only
   * supports limit/page/isRead. A "Transactions" filter can therefore only
   * ever scan a window of recent notifications, never a true category query.
   * Widened from the default dropdown limit (10) to this value — the max the
   * backend's zod schema allows (`limit.max(100)`) — specifically for that
   * filter, and the empty state names this limit explicitly rather than
   * asserting "none exist" when the honest claim is "none in this window".
   * Public: referenced directly from the template.
   */
  readonly TRANSACTIONS_SCAN_LIMIT = 100;

  // Signals
  isOpen = signal(false);
  notifications = signal<Notification[]>([]);
  isLoading = signal(false);
  isMarkingAllRead = signal(false);
  currentFilter = signal<'all' | 'unread' | 'transactions'>('all');
  totalNotifications = signal(0);

  // Computed values
  unreadCount = computed(() =>
    this.notifications().filter(n => !n.isRead).length
  );

  constructor(
    private notificationService: NotificationService,
    private messagingService: MessagingService,
    private elementRef: ElementRef,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadNotifications();

    this.messagingService.message$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        // Optionally convert payload to your Notification interface
        const newNotification: Notification = {
          id: payload.id!,
          title: payload.title,
          message: payload.body,
          type: 'SYSTEM',
          isRead: false,
          createdAt: new Date(),
        };

        this.notifications.update(notifications => [newNotification, ...notifications]);

        // Optionally close dropdown if open
        // this.closeDropdown();
      });

    // Optional: poll for updates if not open
    // setInterval(() => {
    //   if (!this.isOpen()) {
    //     this.loadNotifications();
    //   }
    // }, 30000);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown() {
    this.isOpen.update(open => {
      if (!open) {
        this.loadNotifications();
      }
      return !open;
    });
  }

  closeDropdown(shouldNavigate: boolean = true) {
    if (shouldNavigate) {
      this.router.navigate(['/settings/notifications']);
    }
    this.isOpen.set(false);
  }


  setFilter(filter: 'all' | 'unread' | 'transactions') {
    this.currentFilter.set(filter);
    this.loadNotifications();
  }

  loadNotifications() {
    this.isLoading.set(true);

    const options: NotificationQueryOptions = {
      // Transactions scans a wider window (see TRANSACTIONS_SCAN_LIMIT) since
      // it is filtering by category, not just showing "the last N" verbatim
      // the way all/unread do.
      limit: this.currentFilter() === 'transactions' ? this.TRANSACTIONS_SCAN_LIMIT : 10,
      page: 1
    };

    if (this.currentFilter() === 'unread') {
      options.isRead = false;
    }

    this.notificationService.getNotifications(options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: NotificationQueryResult) => {
          // The list endpoint has no type filter, so "Transactions" is applied
          // client-side over the fetched page rather than via a query param.
          const results = this.currentFilter() === 'transactions'
            ? result.results.filter((n) => this.TRANSACTION_NOTIFICATION_TYPES.includes(n.type))
            : result.results;
          this.notifications.set(results);
          this.totalNotifications.set(this.currentFilter() === 'transactions' ? results.length : result.totalResults);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Failed to load notifications:', error);
          this.isLoading.set(false);
        }
      });
  }

  handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      this.markAsRead(notification.id);
    }

    console.log(notification.entityType, notification.entityId);

    // Navigate to entity if applicable
    if (notification.entityType && notification.entityId) {
      switch (notification.entityType) {
        case 'Lead':
          this.router.navigate([`/leads/${notification.entityId}`]);
          break;
        case 'Client':
          this.router.navigate([`/clients/${notification.entityId}`]);
          break;
        case 'Task':
          this.router.navigate(['/tasks/' + this.gettaskDisplayStyle()], {
            queryParams: { noti_id: notification.entityId },
            queryParamsHandling: 'merge'
          });
          break;
        case 'Opportunity':
          this.router.navigate([`/opportunities/${notification.entityId}`]);
          break;
        case 'Appointment':
          this.router.navigate([`/appointments`], {
            queryParams: { noti_id: notification.entityId },
            queryParamsHandling: 'merge'
          });
          break;
      }
    }

    this.closeDropdown(false);
  }

  gettaskDisplayStyle() {
    return localStorage.getItem(TASK_DISPLAY_STORAGE_KEY) ?? ""
  }

  markAsRead(notificationId: string) {
    // Update locally first for immediate feedback
    this.notifications.update(notifications =>
      notifications.map(n =>
        n.id === notificationId ? { ...n, isRead: true } : n
      )
    );

    // Then update on server
    this.notificationService.markAsRead(notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          console.error('Failed to mark notification as read:', error);
          // Revert the optimistic update on error
          this.loadNotifications();
        }
      });
  }

  markAllAsRead() {
    if (this.unreadCount() === 0) return;

    this.isMarkingAllRead.set(true);

    this.notificationService.markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notifications.update(notifications =>
            notifications.map(n => ({ ...n, isRead: true }))
          );
          this.isMarkingAllRead.set(false);
        },
        error: (error) => {
          console.error('Failed to mark all as read:', error);
          this.isMarkingAllRead.set(false);
        }
      });
  }

  formatTimeAgo(date: string | Date): string {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;

    return notificationDate.toLocaleDateString('en-GB');
  }

  getNotificationIcon(type: string): string {
    const iconMap: Record<string, string> = {
      'info': 'info-circle',
      'warning': 'alert-triangle',
      'error': 'alert-circle',
      'success': 'check-circle',
      'message': 'message-circle',
      'system': 'settings',
      'update': 'download',
      // A client starting a transaction - routine, informational.
      'TRANSACTION_INITIATED': 'bar-chart',
      // A transaction worth a closer look (failed item, redemption, etc.).
      'TRANSACTION_ALERT': 'trending-up'
    };
    return iconMap[type] || 'bell';
  }

  getNotificationColor(type: string): string {
    const colorMap: Record<string, string> = {
      'info': 'blue',
      'warning': 'orange',
      'error': 'red',
      'success': 'green',
      'message': 'purple',
      'system': 'gray',
      'update': 'blue',
      'TRANSACTION_INITIATED': 'blue',
      'TRANSACTION_ALERT': 'orange'
    };
    return colorMap[type] || 'gray';
  }

  trackByNotificationId(index: number, notification: Notification): string {
    return notification.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown(false);
    }
  }
}